import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { RouterProvider } from "react-router";

import Spinner from "@/components/common/Spinner";
import { useAppSelector } from "@/hooks/redux";
import i18n from "@/i18n";
import { getLanguage } from "@/redux/app/appSelector";
import { initializeAuth, setLoading } from "@/redux/auth/authSlice";
import router from "@/router";
import { authService } from "@/services/authService";
import { RootState } from "@/store";

export default function App() {
  const language = useAppSelector(getLanguage);
  const { isLoading } = useAppSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    void i18n.changeLanguage(language);
  }, [language]);

  // Initialize authentication session on app start
  useEffect(() => {
    const initializeSession = async () => {
      dispatch(setLoading(true));
      try {
        const user = await authService.initializeSession();
        if (user) {
          dispatch(initializeAuth(user));
          // console.log("Session restored for:", user.email);
        }
      } catch (error) {
        // console.log("No valid session found");
      } finally {
        dispatch(setLoading(false));
      }
    };

    initializeSession();
  }, [dispatch]);

  // Show loading spinner while initializing session
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full">
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        toastOptions={{
          className: "dark:!bg-gray-800 dark:!text-gray-100",
          duration: 3000,
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
            style: {
              border: "1px solid #ef4444",
            },
          },
          loading: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "#fff",
            },
          },
          style: {
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            color: "#374151",
            fontSize: "14px",
            fontWeight: "500",
            padding: "12px 16px",
            zIndex: 9999,
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
            style: {
              border: "1px solid #10b981",
            },
          },
        }}
      />
    </div>
  );
}
